// 👥 B242 (AI_112) — LISTA CLÍNICA de pacientes (IDENTIFICADA, médico-only).
// Componente "burro": recebe linhas já montadas e as exibe, clicável p/ abrir o
// paciente. É a visão de GESTÃO (com nome) — distinta da pesquisa pseudonimizada.
// A estatística/IA/export NÃO usam isto. Paginação simples (como o sistema antigo).
import React, { useState } from "react";
import { theme } from "../../../shared/styles/theme";

export interface LinhaPaciente {
  id: string;
  nome: string;
  grupo?: string | null;
  status?: string | null; // pre | lista | pos
  rgct?: string;
  dataTx?: string;
  hospital?: string;
  medico?: string;
}

export type ColunaPaciente = "grupo" | "status" | "rgct" | "dataTx" | "hospital" | "medico";

const ROTULO_GRUPO: Record<string, string> = {
  figado: "Fígado",
  rim: "Rim",
  pancreas: "Pâncreas",
  ambulatorial: "Ambulatório",
};
const ROTULO_STATUS: Record<string, string> = {
  pre: "Pré-Tx (não inscrito)",
  lista: "Pré-Tx (inscrito)",
  pos: "Pós-Tx",
};
const CABECALHO: Record<ColunaPaciente, string> = {
  grupo: "Protocolo",
  status: "Situação",
  rgct: "RGCT",
  dataTx: "Data TX",
  hospital: "Hospital TX",
  medico: "Médico responsável",
};

const POR_PAGINA = 50;

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: `2px solid ${theme.gray[200]}`,
  fontSize: "12px",
  fontWeight: 700,
  color: theme.gray[600],
  whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: `1px solid ${theme.gray[100]}`,
  fontSize: "13px",
  color: theme.gray[800],
  whiteSpace: "nowrap",
};

export default function TabelaPacientesPesquisa({
  linhas,
  onAbrir,
  colunas = ["grupo", "status", "rgct"],
}: {
  linhas: LinhaPaciente[];
  onAbrir: (id: string) => void;
  colunas?: ColunaPaciente[];
}) {
  const [pagina, setPagina] = useState(0);
  const totalPaginas = Math.max(1, Math.ceil(linhas.length / POR_PAGINA));
  const pag = Math.min(pagina, totalPaginas - 1);
  const inicio = pag * POR_PAGINA;
  const visiveis = linhas.slice(inicio, inicio + POR_PAGINA);

  const valor = (l: LinhaPaciente, c: ColunaPaciente): string => {
    if (c === "grupo") return l.grupo ? ROTULO_GRUPO[l.grupo] ?? l.grupo : "—";
    if (c === "status") return l.status ? ROTULO_STATUS[l.status] ?? l.status : "—";
    if (c === "rgct") return l.rgct || "—";
    if (c === "dataTx") return l.dataTx || "—";
    if (c === "hospital") return l.hospital || "—";
    return l.medico || "—";
  };

  if (linhas.length === 0) {
    return (
      <p style={{ fontSize: "13px", color: theme.gray[500], margin: "8px 0" }}>
        Nenhum paciente encontrado.
      </p>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          margin: "0 0 8px",
          fontSize: "12px",
          color: theme.gray[600],
        }}
      >
        <span>
          <strong>{linhas.length}</strong> paciente(s)
        </span>
        {totalPaginas > 1 && (
          <span style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto" }}>
            <button
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
              disabled={pag === 0}
              style={btnPag(pag === 0)}
            >
              ‹
            </button>
            Página {pag + 1} de {totalPaginas}
            <button
              onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))}
              disabled={pag >= totalPaginas - 1}
              style={btnPag(pag >= totalPaginas - 1)}
            >
              ›
            </button>
          </span>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Paciente</th>
              {colunas.map((c) => (
                <th key={c} style={th}>
                  {CABECALHO[c]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visiveis.map((l) => (
              <tr key={l.id}>
                <td style={td}>
                  <button
                    onClick={() => onAbrir(l.id)}
                    title="Abrir prontuário do paciente"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: theme.primary[700],
                      fontWeight: 600,
                      fontSize: "13px",
                      cursor: "pointer",
                      textAlign: "left",
                      textDecoration: "underline",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {l.nome || "(sem nome)"}
                  </button>
                </td>
                {colunas.map((c) => (
                  <td key={c} style={td}>
                    {valor(l, c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function btnPag(desabilitado: boolean): React.CSSProperties {
  return {
    padding: "2px 10px",
    border: `1px solid ${theme.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    background: "white",
    color: desabilitado ? theme.gray[300] : theme.gray[700],
    cursor: desabilitado ? "not-allowed" : "pointer",
    fontSize: "14px",
  };
}
